import React, { Suspense, memo, useEffect, useRef } from 'react';

import { View } from '@rbilabs/universal-components';
import { isNil } from 'lodash-es';
import { useIntl } from 'react-intl';
import { Navigate } from 'react-router-dom';
import { isFalse } from 'utils';

import LayoutFabButton from 'components/layout/layout-fab-button';
import useLogPageView from 'components/layout/use-log-page-view';
import { QuestLibrary } from 'components/quests/quests-library';
import { RecentItemUnavailableModal } from 'components/recent-item-unavailable-modal';
import SetLoading from 'components/set-loading';
import { TopServiceModeNotification } from 'components/top-service-mode-notification';
import { useIsTopServiceModeEnabled } from 'experiments/service-mode/use-is-top-servie-mode-enabled';
import useAuth from 'hooks/auth/use-auth';
import useAuthRedirects from 'hooks/auth/use-auth-redirects';
import { useEffectOnUrlChange } from 'hooks/use-effect-on-url-change';
import { LoyaltyOfferDetail } from 'pages/loyalty/loyalty-offers/loyalty-offer-detail';
import LoyaltyOptInModal from 'pages/loyalty/loyalty-opt-in-modal';
import { useEnableStoreLocatorNewUIABTest } from 'pages/store-locator-a-b-test/experiments/use-enable-store-locator-new-ui-a-b-test';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { Route, Routes, useMatch } from 'state/location';
import { useLoyaltyContext } from 'state/loyalty';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useMParticleContext } from 'state/mParticle';
import { useOrderContext } from 'state/order';
import { useScrollContext } from 'state/scroll';
import { getAllLocalizedVersionsOfRoute, routes } from 'utils/routing';

import brandRoutes from './brand-routes';
import { CookieBannerContainer } from './cookie-banner-container';
import { HelmetHTML } from './helmet-html';
import { ScrollContainer } from './scroll-container';
import { LazyRoute, lazyWithFallback } from './util';

const Cart: LazyRoute = lazyWithFallback(() => import('pages/cart'));
const MainMenu: LazyRoute = lazyWithFallback(() => import('pages/main-menu'));
const MenuContent: LazyRoute = lazyWithFallback(() => import('pages/menu-content'));
const Home: LazyRoute = lazyWithFallback(() => import('pages/home'));
const Account: LazyRoute = lazyWithFallback(() => import('pages/account'));
const CommPreferences: LazyRoute = lazyWithFallback(() => import('pages/account/comm-preferences'));
const AccountInfo: LazyRoute = lazyWithFallback(() => import('pages/account/account-info'));
const AccountDelete: LazyRoute = lazyWithFallback(() => import('pages/account/account-delete'));
const AccountRequestInfo: LazyRoute = lazyWithFallback(() =>
  import('pages/account/account-request-info')
);
const PaymentMethods: LazyRoute = lazyWithFallback(() => import('pages/account/payment-methods'));
const AccountPaymentMethods: LazyRoute = lazyWithFallback(() =>
  import('pages/account/payments/payment-methods')
);
const Diagnostics: LazyRoute = lazyWithFallback(() => import('pages/diagnostics'));
const AccountOrders: LazyRoute = lazyWithFallback(() => import('pages/account/account-orders'));
const Offers: LazyRoute = lazyWithFallback(() => import('pages/offers'));
const LoyaltyOffers: LazyRoute = lazyWithFallback(() => import('pages/loyalty/loyalty-offers'));
const LoyaltyRewards: LazyRoute = lazyWithFallback(() => import('pages/loyalty/loyalty-rewards'));
const LoyaltyDashboard: LazyRoute = lazyWithFallback(() =>
  import('pages/loyalty/loyalty-dashboard')
);
const LoyaltySurprise: LazyRoute = lazyWithFallback(() => import('pages/loyalty/loyalty-surprise'));
const LoyaltyInRestaurantRedemption: LazyRoute = lazyWithFallback(() =>
  import('pages/loyalty/loyalty-in-restaurant-redemption')
);
const LoyaltyClaimPoints: LazyRoute = lazyWithFallback(() =>
  import('pages/loyalty/loyalty-claim-points')
);
const LoyaltyLinkCard: LazyRoute = lazyWithFallback(() =>
  import('pages/loyalty/loyalty-link-card')
);
const SignupHome: LazyRoute = lazyWithFallback(() => import('pages/authentication/sign-up-home'));

const AuthChallengeScreenJwt: LazyRoute = lazyWithFallback(() =>
  import('pages/authentication/auth-email-jwt')
);
const ConfirmJwt: LazyRoute = lazyWithFallback(() => import('pages/authentication/confirm-jwt'));
const SignOut: LazyRoute = lazyWithFallback(() => import('pages/authentication/sign-out'));
const StoreLocator: LazyRoute = lazyWithFallback(() => import('pages/store-locator'));
const StoreLocatorExperiment: LazyRoute = lazyWithFallback(() =>
  import('pages/store-locator-a-b-test')
);
const OrderConfirmation: LazyRoute = lazyWithFallback(() =>
  import('pages/order-confirmation/order-confirmation')
);
const StaticPage: LazyRoute = lazyWithFallback(() => import('pages/static'));
const NotFound: LazyRoute = lazyWithFallback(() => import('pages/not-found'));
const Pypestream: LazyRoute = lazyWithFallback(() => import('pages/pypestream'));
const SupportV2CategoriesPage: LazyRoute = lazyWithFallback(() =>
  import('pages/support-v2/support-v2-categories-page')
);
const SupportV2CategoryPage: LazyRoute = lazyWithFallback(() =>
  import('pages/support-v2/support-v2-category-page')
);
const SupportV2FormPage: LazyRoute = lazyWithFallback(() =>
  import('pages/support-v2/support-v2-form-page')
);
const SupportV2OrdersListPage: LazyRoute = lazyWithFallback(() =>
  import('pages/support-v2/support-v2-orders-list-page')
);
const ReportOrderIssuePage: LazyRoute = lazyWithFallback(() => import('pages/report-order-issue'));
const ReportOrderNeverArrivedPage: LazyRoute = lazyWithFallback(() =>
  import('pages/report-order-never-arrived')
);
const ReportOrderItemsMissingPage: LazyRoute = lazyWithFallback(() =>
  import('pages/report-order-items-missing')
);
const History = lazyWithFallback(() => import('pages/account/history'));
const FavoriteEditorPage = lazyWithFallback(() => import('pages/favorite-editor'));

// OrderContext changes a lot, and we should isolate the effect of re-rendering
// into isolation. We do not want the OrderContext to cause a re-render of the entire layout.
const ClearServerOrderContainer = () => {
  const { clearServerOrder } = useOrderContext(); // @todo type order context

  useEffect(() => {
    // reset the server order when a user navigate into the menu no matter what
    clearServerOrder();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

const ContentRoutingContainer = () => {
  // useGoogleAnalytics();
  // TODO: RN - web - may need to add this back for web, but removing for now
  // useSetUserLocale();

  const { formatMessage } = useIntl();
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { claimPointsEnabled, loyaltySurpriseOffersEnabled } = useLoyaltyContext();

  const enableSignUpFlag = useFlag(LaunchDarklyFlag.ENABLE_SIGN_UP);
  const enableSignUp = isNil(enableSignUpFlag) || enableSignUpFlag;
  const forceAuthOnHomepageFlag = useFlag(LaunchDarklyFlag.FORCE_HOMEPAGE_AUTH);
  const isBaseRoute = useMatch(routes.base);
  const forceAuthOnHomepage = forceAuthOnHomepageFlag && enableSignUp && isBaseRoute;

  useAuthRedirects({ skip: !forceAuthOnHomepage });

  return (
    <>
      <ClearServerOrderContainer />
      <ContentRouting
        formatMessage={formatMessage}
        claimPointsEnabled={claimPointsEnabled}
        loyaltyEnabled={loyaltyEnabled}
        loyaltySurpriseOffersEnabled={loyaltySurpriseOffersEnabled}
      />
    </>
  );
};

type ContentRoutingProps = {
  formatMessage: ReturnType<typeof useIntl>['formatMessage'];
  claimPointsEnabled: boolean;
  loyaltyEnabled: boolean;
  loyaltySurpriseOffersEnabled: boolean;
};

const ContentRouting = memo(
  ({
    formatMessage,
    claimPointsEnabled,
    loyaltyEnabled,
    loyaltySurpriseOffersEnabled,
  }: ContentRoutingProps) => {
    const enableAccountDeletePage = useFlag(LaunchDarklyFlag.ENABLE_ACCOUNT_DELETE_PAGE);
    const enableOrderSupport = useFlag(LaunchDarklyFlag.ENABLE_ORDER_SUPPORT);
    const enableRequestMyInformation = useFlag(LaunchDarklyFlag.ENABLE_REQUEST_MY_INFORMATION);
    const enableCommPrefFlag = useFlag(LaunchDarklyFlag.ENABLE_COMMUNICATION_PREFERENCES);
    const enableCommunicationPreferences = !isFalse(enableCommPrefFlag);
    const enablePypestream = useFlag(LaunchDarklyFlag.ENABLE_PYPESTREAM);
    const enableSignUpFlag = useFlag(LaunchDarklyFlag.ENABLE_SIGN_UP);
    const enableSignUp = isNil(enableSignUpFlag) || enableSignUpFlag;
    const enableRewardPageStandAlone = useFlag(LaunchDarklyFlag.ENABLE_REWARD_PAGE_STAND_ALONE);
    const enableRewardsStaticPage = useFlag(LaunchDarklyFlag.ENABLE_REWARDS_STATIC_PAGE);
    const enableOffersStaticPage = useFlag(LaunchDarklyFlag.ENABLE_OFFERS_STATIC_PAGE);
    const enableQuests = useFlag(LaunchDarklyFlag.ENABLE_QUEST_TILES);
    const enableNewPaymentsFrontend = useFlag(LaunchDarklyFlag.ENABLE_NEW_PAYMENTS_FRONTEND);
    const isOffersV2Enabled = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_OFFERS_V2_FE);
    const enableGuestAndMultiStepSupportForm = useFlag(
      LaunchDarklyFlag.ENABLE_GUEST_AND_MULTI_STEP_SUPPORT_PAGE
    );

    return (
      <Routes>
        <Route path={'/:staticPagePath'} element={<StaticPage />} />
        <Route path={routes.notFound404} element={<NotFound />} />
        <Route path={routes.confirmJwt} element={<ConfirmJwt />} />
        {loyaltyEnabled && (
          <Route
            path={`${formatMessage({ id: 'routes.loyaltyDashboard' })}`}
            element={<LoyaltyDashboard />}
          />
        )}
        {/* TODO: This whole feature was build for PLK - Analyze if we can clean this up */}
        {loyaltySurpriseOffersEnabled && (
          <Route
            path={`${formatMessage({ id: 'routes.loyaltyHome' })}/${formatMessage({
              id: 'surprise',
            })}`}
            element={<LoyaltySurprise />}
          />
        )}
        <Route
          path={formatMessage({ id: 'routes.redemptionInRestaurant' })}
          element={<LoyaltyInRestaurantRedemption />}
        />
        <Route
          path={formatMessage({ id: 'routes.linkPhysicalCard' })}
          element={<LoyaltyLinkCard />}
        />
        {claimPointsEnabled && (
          <Route
            path={`${formatMessage({ id: 'routes.claimPointsBase' })}/*`}
            element={<LoyaltyClaimPoints />}
          />
        )}
        {enableSignUp && (
          <>
            <Route path={formatMessage({ id: 'routes.signUp' })} element={<SignupHome />} />
            {getAllLocalizedVersionsOfRoute('signUp').map((path, index) => (
              <Route path={path} key={`sign-up-${index}`} element={<SignupHome />} />
            ))}
            <Route path={formatMessage({ id: 'routes.signIn' })} element={<SignupHome />} />
            {getAllLocalizedVersionsOfRoute('signIn').map((path, index) => (
              <Route path={path} key={`sign-in-${index}`} element={<SignupHome />} />
            ))}
            <Route path={routes.confirmOtp} element={<SignupHome />} />
            <Route path={formatMessage({ id: 'routes.account' })} element={<Account />} />
            {getAllLocalizedVersionsOfRoute('account').map((path, index) => (
              <Route path={path} key={`account-${index}`} element={<Account />} />
            ))}
          </>
        )}
        <Route path={routes.authChallengeJwt} element={<AuthChallengeScreenJwt />} />
        <Route path={routes.signOut} element={<SignOut />} />
        <Route path={routes.menu} element={<MainMenu />} />
        <Route path={routes.browseMenu} element={<MainMenu />} />
        {/*
                MenuContent handles the following routes:

                /menu/favorites
                /menu/recent-items
                /menu/section-:id
                /menu/picker-:id
                /menu/combo-:id
                /menu/item-:id
                /menu/* (Item Not Found)

              */}
        <Route path={`${routes.menu}/:id`} element={<MenuContent />} />
        <Route path={`${routes.browseMenu}/:id`} element={<MenuContent />} />
        <Route path={routes.base} element={<Home />} />
        {!enableOffersStaticPage && <Route path={`${routes.offers}/*`} element={<Offers />} />}
        {enableRewardPageStandAlone && (
          <Route
            path={formatMessage({ id: 'routes.loyaltyHome' })}
            element={<LoyaltyDashboard />}
          />
        )}
        {!enableRewardsStaticPage && (
          <>
            <Route
              path={`${formatMessage({ id: 'routes.loyaltyOfferList' })}`}
              element={<LoyaltyOffers />}
            />
            <Route
              path={`${formatMessage({ id: 'routes.loyaltyOfferList' })}/:offerEngineId`}
              element={isOffersV2Enabled ? <LoyaltyOfferDetail /> : <LoyaltyOffers />}
            />
            <Route
              path={formatMessage({ id: 'routes.loyaltyRewardList' })}
              element={<LoyaltyRewards />}
            />
            <Route
              path={`${formatMessage({ id: 'routes.loyaltyRewardList' })}/:rewardId`}
              element={<LoyaltyRewards />}
            />
          </>
        )}

        <Route path={routes.accountInfo} element={<AccountInfo />} />
        <Route path={`${formatMessage({ id: 'routes.rewardsHistory' })}`} element={<History />} />
        {enableCommunicationPreferences && (
          <Route path={routes.communication} element={<CommPreferences />} />
        )}
        <Route path={routes.orders} element={<AccountOrders />} />
        <Route path={`${routes.orders}/:rbiOrderId`} element={<AccountOrders />} />
        {enableAccountDeletePage && (
          <Route path={formatMessage({ id: 'routes.accountDelete' })} element={<AccountDelete />} />
        )}
        {enableRequestMyInformation && (
          <Route
            path={formatMessage({ id: 'routes.accountRequestInfo' })}
            element={<AccountRequestInfo />}
          />
        )}
        <Route
          path={`${routes.payment}/*`}
          element={enableNewPaymentsFrontend ? <AccountPaymentMethods /> : <PaymentMethods />}
        />
        <Route path={`${routes.diagnostics}/*`} element={<Diagnostics />} />
        {enableOrderSupport && (
          <Route path={`${routes.reportOrderIssue}/:orderId`} element={<ReportOrderIssuePage />} />
        )}
        {enableOrderSupport && (
          <Route
            path={`${routes.missingOrder}/:orderId`}
            element={<ReportOrderNeverArrivedPage />}
          />
        )}
        {enableOrderSupport && (
          <Route
            path={`${routes.missingItems}/:orderId`}
            element={<ReportOrderItemsMissingPage />}
          />
        )}
        {enablePypestream ? (
          // show pypestream and redirect all other support links to pypestream
          <>
            <Route path={routes.support} element={<Pypestream />} />
            <Route
              path={`${routes.support}/*`}
              element={<Navigate replace to={routes.support} />}
            />
          </>
        ) : enableGuestAndMultiStepSupportForm ? (
          // general support requests
          <>
            {/* // general support requests */}
            <Route path={routes.supportForm} element={<SupportV2FormPage />} />
            <Route path={routes.supportOrders} element={<SupportV2OrdersListPage />} />
            <Route path={routes.supportCategories} element={<SupportV2CategoriesPage />} />
            <Route path={routes.supportCategory} element={<SupportV2CategoryPage />} />
            <Route path={routes.support} element={<SupportV2CategoriesPage />} />
          </>
        ) : null}

        <Route path={routes.favoriteEditor} element={<FavoriteEditorPage />} />

        {/* We only want brand-specific routes to be available for those brands */}
        {/* `brandRoutes` can be thought of a Map of routes (string) to Components (React component) */}
        {Object.entries(brandRoutes).map(([path, Component]) => (
          <Route path={formatMessage({ id: path })} element={<Component key={path} />} key={path} />
        ))}
        {enableQuests && <Route path={routes.questLibrary} element={<QuestLibrary />} />}
      </Routes>
    );
  }
);

export default function LayoutContainerComponent() {
  const { user } = useAuth();
  const hasUpdatedUserAttributes = useRef(false);
  const { updateUserAttributes } = useMParticleContext();
  const { scrollTo, scrollRef } = useScrollContext();
  const enableTopServiceMode = useIsTopServiceModeEnabled();
  const enableStoreLocatorExperiment = useEnableStoreLocatorNewUIABTest();

  // Updates user attributes once on initial load.
  // There was previously a bug introduced which did not correctly set Braze user attributes.
  // To respect users' communication preferences, we must temporarily leverage this code.
  // Ticket BKPE-1868 was created to remove this code in 3 months time.
  // After which - user attributes will update normally (upon request in the /account/info page).
  if (user?.details.email && !hasUpdatedUserAttributes.current) {
    updateUserAttributes(user.details);
    hasUpdatedUserAttributes.current = true;
  }
  // Track all page views on mParticle
  useLogPageView();

  useEffectOnUrlChange(() => {
    scrollTo({ animated: false });
  });

  const { formatMessage } = useIntl();

  return (
    <>
      <View flex="1" overflowX="hidden">
        <ScrollContainer scrollRef={scrollRef}>
          <Suspense fallback={<SetLoading />}>
            <Routes>
              <Route path={`${routes.cart}/*`} element={<Cart />} />
              <Route
                path={`${routes.orderConfirmation}/:rbiOrderId`}
                element={<OrderConfirmation />}
              />
              <Route
                path={`${routes.storeLocator}/*`}
                element={
                  enableStoreLocatorExperiment ? <StoreLocatorExperiment /> : <StoreLocator />
                }
              />
              <Route
                path={`${formatMessage({ id: 'routes.storeLocator' })}/*`}
                element={<StoreLocator />}
              />
              {/* Regular routes (with normal nav, header, footer, etc) */}
              <Route path="*" element={<ContentRoutingContainer />} />
            </Routes>
          </Suspense>
          {/* Modals and things that don't render "In flow" */}
          <HelmetHTML />
          <LoyaltyOptInModal />
          <RecentItemUnavailableModal />
        </ScrollContainer>
        <View>
          <LayoutFabButton />
          <CookieBannerContainer />
        </View>
      </View>
      <View>{enableTopServiceMode && <TopServiceModeNotification />}</View>
    </>
  );
}
